import { useState, type ReactNode } from 'react';
import { ImageOff } from 'lucide-react';
import { useFileSrc } from '../platform/files/uploads.ts';

/**
 * Image for an uploaded file with graceful fallbacks: local copy (not yet
 * uploaded) → cached thumbnail → original → placeholder (or `fallback`).
 */
export function FileThumb({
  fileId,
  alt,
  className,
  placeholderClassName,
  fallback,
}: {
  fileId: string | null | undefined;
  alt: string;
  className?: string;
  placeholderClassName?: string;
  /** Shown instead of the generic "no image" icon, e.g. a pet's species emoji. */
  fallback?: ReactNode;
}) {
  const thumb = useFileSrc(fileId, 'thumb');
  const original = useFileSrc(fileId, 'original');
  const [stage, setStage] = useState<'thumb' | 'original' | 'failed'>('thumb');
  const src = stage === 'thumb' ? thumb : stage === 'original' ? original : undefined;
  if (!fileId || stage === 'failed' || !src) {
    return (
      <span
        className={placeholderClassName ?? className}
        role="img"
        aria-label={alt || 'No image'}
        style={{ display: 'grid', placeItems: 'center', background: 'var(--color-sunken)', color: 'var(--color-text-muted)', fontSize: '1.8rem' }}
      >
        {fallback ?? <ImageOff aria-hidden="true" width={24} height={24} />}
      </span>
    );
  }
  return (
    <img className={className} src={src} alt={alt} loading="lazy" decoding="async" onError={() => setStage((s) => (s === 'thumb' ? 'original' : 'failed'))} />
  );
}
